import { ICON_CLOSE } from './icons.js';
import { fetchVoices, type VoiceCatalogueEntry } from './api.js';
import { type TranslateFn, type UiLang, detectInitialLang } from './i18n.js';

const STORAGE_KEY = 'llais.settings';

export type Settings = {
  voice: string;
  speed: number;
  translate: boolean;
  translateTo: string;
  karaoke: boolean;
  uiLang: UiLang;
  // Phase A page-context: when on, the widget sends page metadata
  // (lang, title, headings) with each /v1/speak call so the backend
  // can bias voice selection. Default is computed from the API key
  // prefix - see SettingsCallbacks.defaultPageContextEnabled.
  pageContextEnabled: boolean;
};

export type SettingsCallbacks = {
  apiBase: string;
  defaultVoice: string;
  defaultPageContextEnabled: boolean;
  initialTranslator: TranslateFn;
  onChange: (s: Settings) => void;
  onClose: () => void;
};

const DEFAULT: Settings = {
  voice: 'nia',
  speed: 1,
  translate: false,
  translateTo: 'en',
  karaoke: false,
  uiLang: 'en',
  pageContextEnabled: false, // overridden by cb.defaultPageContextEnabled at construction
};

// Capsiynau /v1/translate accepts cy and en. More languages need upstream
// support before they can be surfaced here.
const TRANSLATE_LANGS: Array<{ id: string; label: string }> = [
  { id: 'en', label: 'English' },
  { id: 'cy', label: 'Cymraeg' },
];

export class SettingsPanel {
  private el: HTMLDivElement;
  private settings: Settings;
  private voicesLoaded = false;
  private isOpen = false;
  private tr: TranslateFn;
  // Voice catalogue cached after first fetch so language flips re-render
  // groupings without a re-fetch.
  private voiceCatalogue: VoiceCatalogueEntry[] = [];

  constructor(private cb: SettingsCallbacks) {
    this.tr = cb.initialTranslator;
    // Smart defaults based on the host page's lang attribute.
    //
    // karaoke defaults ON globally - it's a core widget UX even on
    // English pages and costs nothing extra to surface.
    //
    // translate defaults OFF on every page. The previous version (PR #29)
    // turned this ON for Welsh pages, but that's overaggressive: selecting
    // Welsh text on a Welsh page and translating it TO Welsh is an
    // identity translation - wastes upstream OpenAI tokens (Capsiynau's
    // /v1/translate is OpenAI-backed) and cascades 429s back to the
    // widget when OpenAI quota is exhausted. translateTo still defaults
    // to 'cy' on Welsh pages so users who manually flip translate ON get
    // sensible behaviour straight away.
    //
    // Restore (localStorage) at the end means explicit user toggles
    // always win.
    const detectedLang = detectInitialLang();
    const isWelshPage = detectedLang === 'cy';
    this.settings = {
      ...DEFAULT,
      voice: cb.defaultVoice,
      uiLang: detectedLang,
      karaoke: true,
      translateTo: isWelshPage ? 'cy' : 'en',
      pageContextEnabled: cb.defaultPageContextEnabled,
      ...this.restore(),
    };

    this.el = document.createElement('div');
    this.el.className = 'settings';
    this.el.setAttribute('role', 'dialog');
    this.el.setAttribute('aria-label', 'Llais');
    this.el.setAttribute('aria-hidden', 'true');
    this.render();
    this.bindEvents();

    void this.populateVoices();
  }

  get root(): HTMLElement {
    return this.el;
  }

  get value(): Settings {
    return this.settings;
  }

  open(): void {
    this.isOpen = true;
    this.el.classList.add('open');
    this.el.setAttribute('aria-hidden', 'false');
    const close = this.el.querySelector<HTMLButtonElement>('.settings-close');
    close?.focus();
  }

  close(): void {
    this.isOpen = false;
    this.el.classList.remove('open');
    this.el.setAttribute('aria-hidden', 'true');
    this.cb.onClose();
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  refreshLabels(tr: TranslateFn): void {
    this.tr = tr;
    this.render();
    this.bindEvents();
    if (this.voicesLoaded) this.applyVoiceOptions();
  }

  // Render the panel's body. Called from constructor + on language change.
  private render(): void {
    const tr = this.tr;
    this.el.innerHTML = `
      <header class="settings-header">
        <strong>${tr('settings')}</strong>
        <button class="settings-close" aria-label="${tr('closeSettings')}">${ICON_CLOSE}</button>
      </header>
      <div class="settings-body">
        <label class="settings-row settings-row-toggle">
          <span class="settings-label">${tr('language')}</span>
          <div class="settings-langtoggle" role="radiogroup" aria-label="${tr('language')}">
            <button type="button" data-lang="en" class="${this.settings.uiLang === 'en' ? 'sel' : ''}" role="radio" aria-checked="${this.settings.uiLang === 'en'}">EN</button>
            <button type="button" data-lang="cy" class="${this.settings.uiLang === 'cy' ? 'sel' : ''}" role="radio" aria-checked="${this.settings.uiLang === 'cy'}">CY</button>
          </div>
        </label>
        <label class="settings-row">
          <span class="settings-label">${tr('voice')}</span>
          <select class="settings-voice" aria-label="${tr('voice')}">
            <option value="${this.settings.voice}" selected>${this.settings.voice}</option>
          </select>
        </label>
        <label class="settings-row">
          <span class="settings-label">${tr('speed')}</span>
          <div class="settings-speed" role="radiogroup" aria-label="${tr('speed')}">
            ${[0.8, 1, 1.2, 1.4].map((s) => `
              <button type="button" data-speed="${s}" class="${s === this.settings.speed ? 'sel' : ''}" role="radio" aria-checked="${s === this.settings.speed}">${s === 1 ? '1×' : `${s}×`}</button>
            `).join('')}
          </div>
        </label>
        <label class="settings-row settings-row-toggle">
          <span class="settings-label">${tr('translateSelection')}</span>
          <input type="checkbox" class="settings-translate" ${this.settings.translate ? 'checked' : ''} />
        </label>
        <label class="settings-row${this.settings.translate ? '' : ' settings-row-disabled'}">
          <span class="settings-label">${tr('translateTo')}</span>
          <select class="settings-translate-to" aria-label="${tr('translateTo')}"${this.settings.translate ? '' : ' disabled'}>
            ${TRANSLATE_LANGS.map((l) => `<option value="${l.id}" ${l.id === this.settings.translateTo ? 'selected' : ''}>${l.label}</option>`).join('')}
          </select>
        </label>
        <label class="settings-row settings-row-toggle">
          <span class="settings-label">
            ${tr('readAlong')}
            <span class="settings-hint-inline">${tr('readAlongHint')}</span>
          </span>
          <input type="checkbox" class="settings-karaoke" ${this.settings.karaoke ? 'checked' : ''} />
        </label>
        <label class="settings-row settings-row-toggle">
          <span class="settings-label">
            ${tr('pageContextToggle')}
            <span class="settings-hint-inline">${tr('pageContextHint')}</span>
          </span>
          <input type="checkbox" class="settings-page-context" ${this.settings.pageContextEnabled ? 'checked' : ''} />
        </label>
      </div>
      <footer class="settings-footer">
        <span class="settings-hint">${tr('footerHint')}</span>
        <a class="settings-credit" href="https://capsiynau.com" target="_blank" rel="noopener noreferrer">
          ${tr('poweredBy')} <strong>Capsiynau</strong>
        </a>
      </footer>
    `;
  }

  private bindEvents(): void {
    this.el.querySelector('.settings-close')!.addEventListener('click', () => this.close());

    const voiceSel = this.el.querySelector<HTMLSelectElement>('.settings-voice')!;
    voiceSel.addEventListener('change', () => {
      this.settings.voice = voiceSel.value;
      this.persist();
      this.cb.onChange(this.settings);
    });

    this.el.querySelectorAll<HTMLButtonElement>('.settings-speed button').forEach((btn) => {
      btn.addEventListener('click', () => {
        const speed = Number(btn.dataset.speed);
        this.settings.speed = speed;
        this.el.querySelectorAll<HTMLButtonElement>('.settings-speed button').forEach((b) => {
          const sel = Number(b.dataset.speed) === speed;
          b.classList.toggle('sel', sel);
          b.setAttribute('aria-checked', sel ? 'true' : 'false');
        });
        this.persist();
        this.cb.onChange(this.settings);
      });
    });

    this.el.querySelectorAll<HTMLButtonElement>('.settings-langtoggle button').forEach((btn) => {
      btn.addEventListener('click', () => {
        const lang = btn.dataset.lang as UiLang;
        if (lang !== 'en' && lang !== 'cy') return;
        this.settings.uiLang = lang;
        this.persist();
        // Update sel state on the toggle without re-rendering yet —
        // onChange will trigger main.ts to call refreshLabels() which
        // does the full re-render.
        this.el.querySelectorAll<HTMLButtonElement>('.settings-langtoggle button').forEach((b) => {
          const sel = b.dataset.lang === lang;
          b.classList.toggle('sel', sel);
          b.setAttribute('aria-checked', sel ? 'true' : 'false');
        });
        this.cb.onChange(this.settings);
      });
    });

    const translateChk = this.el.querySelector<HTMLInputElement>('.settings-translate')!;
    // The settings template renders 5+ rows; index [4] is the translate row.
    // Non-null assertion matches the style used for the other querySelector
    // bindings in this file and silences TS18048 without changing behaviour.
    const translateRow = this.el.querySelectorAll<HTMLElement>('.settings-row')[4]!;
    const translateToSel = this.el.querySelector<HTMLSelectElement>('.settings-translate-to')!;
    translateChk.addEventListener('change', () => {
      this.settings.translate = translateChk.checked;
      translateRow.classList.toggle('settings-row-disabled', !this.settings.translate);
      translateToSel.disabled = !this.settings.translate;
      this.persist();
      this.cb.onChange(this.settings);
    });
    translateToSel.addEventListener('change', () => {
      this.settings.translateTo = translateToSel.value;
      this.persist();
      this.cb.onChange(this.settings);
    });

    const karaokeChk = this.el.querySelector<HTMLInputElement>('.settings-karaoke')!;
    karaokeChk.addEventListener('change', () => {
      this.settings.karaoke = karaokeChk.checked;
      this.persist();
      this.cb.onChange(this.settings);
    });

    const pageCtxChk = this.el.querySelector<HTMLInputElement>('.settings-page-context')!;
    pageCtxChk.addEventListener('change', () => {
      this.settings.pageContextEnabled = pageCtxChk.checked;
      this.persist();
      this.cb.onChange(this.settings);
    });
  }

  private restore(): Partial<Settings> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as Partial<Settings>;
      return parsed;
    } catch {
      return {};
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch {
      /* ignore */
    }
  }

  private async populateVoices(): Promise<void> {
    if (this.voicesLoaded) return;
    const voices = await fetchVoices(this.cb.apiBase);
    const available = voices.filter((v) => v.available);
    if (available.length === 0) return;
    this.voiceCatalogue = available;
    this.voicesLoaded = true;
    this.applyVoiceOptions();

    if (!available.some((v) => v.id === this.settings.voice)) {
      this.settings.voice = available[0]?.id ?? this.cb.defaultVoice;
      const sel = this.el.querySelector<HTMLSelectElement>('.settings-voice');
      if (sel) sel.value = this.settings.voice;
      this.persist();
      this.cb.onChange(this.settings);
    }
  }

  // Build the voice select <optgroup>s using the cached catalogue, using
  // translated group labels. Called on first fetch + on language change.
  private applyVoiceOptions(): void {
    if (this.voiceCatalogue.length === 0) return;
    const cy = this.tr('languageWelsh');
    const multiLabel = this.tr === undefined ? 'Multilingual' : (this.settings.uiLang === 'cy' ? 'Aml-iaith' : 'Multilingual');

    const groups = new Map<string, VoiceCatalogueEntry[]>();
    for (const v of this.voiceCatalogue) {
      const key = v.language === 'cy-GB' ? cy : multiLabel;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(v);
    }

    const isWelshUi = this.settings.uiLang === 'cy';
    const html: string[] = [];
    for (const [groupLabel, items] of groups) {
      html.push(`<optgroup label="${escapeAttr(groupLabel)}">`);
      for (const v of items) {
        const selected = v.id === this.settings.voice ? ' selected' : '';
        // Prefer the backend-supplied Welsh label when the UI is in cy mode;
        // fall back to the English `label` if the backend hasn't shipped a
        // label_cy yet (older deploys).
        const label = isWelshUi && v.label_cy ? v.label_cy : v.label;
        html.push(`<option value="${v.id}"${selected}>${escapeAttr(label)}</option>`);
      }
      html.push('</optgroup>');
    }
    const sel = this.el.querySelector<HTMLSelectElement>('.settings-voice');
    if (sel) sel.innerHTML = html.join('');
  }
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
}
